<template>
    <v-container fluid class="mb-12 mb-lg-0">
        <v-row justify="center">
            <v-col cols="12">
                <v-card width="100%">
                    <v-card-title class="d-block text-center font-weight-bold primary secondary--text py-2" style="word-break: normal">
                        CLIENTES EN LINEA
                    </v-card-title>
                    <v-btn bottom color="secondary primary--text" depressed fab absolute right small :loading="loading" @click="getReport">
                        <v-icon>mdi-sync</v-icon>
                    </v-btn>
                </v-card>
            </v-col>
        </v-row>

        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-title>
                        <v-text-field v-model="search" append-icon="mdi-magnify" label="Buscar" single-line hide-details/>
                        <v-spacer/>
                        <v-chip small label color="primary" class="secondary--text" v-if="lastRefresh">
                            <v-icon left small>mdi-clock-outline</v-icon>{{ lastRefresh }}
                        </v-chip>
                    </v-card-title>
                    <v-data-table
                        :headers="headers"
                        :items="items"
                        :search="search"
                        :loading="loading"
                        :footer-props="footerProps"
                        no-data-text="No hay clientes activos en este momento">
                        <template v-slot:item.id="{ item }">
                            <strong>{{ String(item.id).padStart(5, '0') }}</strong>
                        </template>
                        <template v-slot:item.name="{ item }">
                            <div class="font-weight-bold">{{ item.name }}</div>
                            <div class="caption grey--text text--darken-1">{{ item.email }}</div>
                        </template>
                        <template v-slot:item.nickname="{ item }">
                            <v-chip small label color="secondary" class="primary--text font-weight-bold">
                                {{ item.nickname || 'Sin ID' }}
                            </v-chip>
                        </template>
                        <template v-slot:item.status_label="{ item }">
                            <v-chip small label :color="statusColor(item.status)" dark>
                                {{ item.status_label }}
                            </v-chip>
                        </template>
                        <template v-slot:item.last_action="{ item }">
                            <div>{{ item.last_action }}</div>
                            <div class="caption grey--text text--darken-1" v-if="item.url">{{ item.url }}</div>
                        </template>
                        <template v-slot:item.actions="{ item }">
                            <v-btn small color="primary" outlined :loading="historyLoading === item.id" @click="openHistory(item)">
                                <v-icon left small>mdi-history</v-icon>Ver
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="historyDialog" scrollable max-width="980px">
            <v-card class="audit-fixed-dialog">
                <v-card-title class="primary secondary--text" style="word-break: normal">
                    <v-icon color="secondary" class="mr-2">mdi-account-clock</v-icon>
                    {{ selectedCustomer ? selectedCustomer.name : 'Actividad del cliente' }}
                    <v-spacer/>
                    <v-btn icon color="secondary" @click="historyDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="audit-fixed-body pa-0">
                    <v-tabs v-model="historyTab" background-color="transparent" color="primary" grow>
                        <v-tab href="#activity">
                            <v-icon left small>mdi-history</v-icon>Actividad
                        </v-tab>
                        <v-tab href="#devices">
                            <v-icon left small>mdi-devices</v-icon>Dispositivos
                        </v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="historyTab">
                        <v-tab-item value="activity">
                            <v-data-table
                                :headers="activityHeaders"
                                :items="activity"
                                :loading="historyLoading !== null"
                                :footer-props="footerProps"
                                no-data-text="No hay actividad registrada">
                                <template v-slot:item.result="{ item }">
                                    <v-chip x-small label :color="item.result === 'Correcto' ? 'success' : 'error'" dark>
                                        {{ item.result }}
                                    </v-chip>
                                </template>
                                <template v-slot:item.action="{ item }">
                                    <div class="font-weight-bold">{{ item.action }}</div>
                                    <div class="caption grey--text text--darken-1" v-if="item.details">{{ item.details }}</div>
                                </template>
                            </v-data-table>
                        </v-tab-item>
                        <v-tab-item value="devices">
                            <v-data-table
                                :headers="deviceHeaders"
                                :items="devices"
                                :loading="historyLoading !== null"
                                :footer-props="footerProps"
                                no-data-text="No hay dispositivos registrados">
                                <template v-slot:item.name="{ item }">
                                    <div class="font-weight-bold">{{ item.name }}</div>
                                    <div class="caption grey--text text--darken-1">{{ item.app }}</div>
                                </template>
                                <template v-slot:item.status="{ item }">
                                    <v-chip x-small label :color="item.status_key === 'active' ? 'success' : 'grey'" dark>
                                        {{ item.status }}
                                    </v-chip>
                                    <v-chip v-if="item.hidden_from_customer" x-small label color="warning" dark class="ml-1">
                                        Oculto
                                    </v-chip>
                                </template>
                                <template v-slot:item.security="{ item }">
                                    <v-chip x-small label :color="item.secure_device ? 'success' : 'grey'" dark class="mr-1">
                                        {{ item.secure_device ? 'Seguro' : 'Sin ID seguro' }}
                                    </v-chip>
                                    <v-chip x-small label :color="item.dynamic_key_enabled ? 'primary' : 'grey'" dark>
                                        {{ item.dynamic_key_enabled ? 'Clave activa' : 'Sin clave' }}
                                    </v-chip>
                                </template>
                                <template v-slot:item.revoked="{ item }">
                                    <div>{{ item.revoked_at || '-' }}</div>
                                    <div class="caption grey--text text--darken-1" v-if="item.revoked_by">{{ item.revoked_by }}</div>
                                </template>
                            </v-data-table>
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>
                <v-divider/>
                <v-card-actions class="audit-fixed-footer">
                    <v-btn text color="primary" @click="historyDialog = false">Cerrar</v-btn>
                    <v-spacer/>
                    <v-btn color="primary" class="secondary--text" :loading="historyLoading !== null" @click="refreshHistory">
                        <v-icon left>mdi-sync</v-icon>Actualizar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';

export default {
    name: "CustomersOnline",
    props: ['user'],
    data: () => ({
        loading: true,
        historyLoading: null,
        search: '',
        items: [],
        selectedCustomer: null,
        historyDialog: false,
        historyTab: 'activity',
        activity: [],
        devices: [],
        refreshTimer: null,
        lastRefresh: '',
        headers: [
            { text: 'ID', value: 'id', sortable: false },
            { text: 'Cliente', value: 'name', sortable: false },
            { text: 'ID Valzum', value: 'nickname', sortable: false },
            { text: 'Estado', value: 'status_label', sortable: false },
            { text: 'Ultima accion', value: 'last_action', sortable: false },
            { text: 'IP', value: 'ip', sortable: false },
            { text: 'Visto', value: 'last_seen', sortable: false },
            { text: '', value: 'actions', sortable: false, align: 'right' },
        ],
        activityHeaders: [
            { text: 'Fecha', value: 'created_at', sortable: false },
            { text: 'Tipo', value: 'type', sortable: false },
            { text: 'Accion', value: 'action', sortable: false },
            { text: 'Resultado', value: 'result', sortable: false },
            { text: 'IP', value: 'ip', sortable: false },
        ],
        deviceHeaders: [
            { text: 'Dispositivo', value: 'name', sortable: false },
            { text: 'Estado', value: 'status', sortable: false },
            { text: 'Seguridad', value: 'security', sortable: false },
            { text: 'IP', value: 'last_ip', sortable: false },
            { text: 'Ultimo login', value: 'last_login_at', sortable: false },
            { text: 'Usos', value: 'login_count', sortable: false },
            { text: 'Retirado', value: 'revoked', sortable: false },
        ],
        footerProps: {
            itemsPerPageOptions: [25, 50, 100, 200, -1]
        }
    }),
    mounted() {
        this.getReport();
    },
    destroyed() {
        clearTimeout(this.refreshTimer);
    },
    methods: {
        getReport(silent = false) {
            clearTimeout(this.refreshTimer);
            if (!silent) {
                this.loading = true;
            }
            axios.get('/api/reports/customers-online').then(res => {
                this.items = res.data.items || [];
                this.lastRefresh = new Date().toLocaleTimeString('es-CO', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }).catch(err => {
                if (!silent) {
                    this.$toast.error(this.errorMessage(err, 'No fue posible cargar clientes en linea.'), {icon: 'mdi-alert-circle'});
                }
            }).then(() => {
                this.loading = false;
                this.refreshTimer = setTimeout(() => this.getReport(true), 300000);
            });
        },
        openHistory(customer) {
            this.selectedCustomer = customer;
            this.historyDialog = true;
            this.historyTab = 'activity';
            this.loadHistory(customer.id);
        },
        refreshHistory() {
            if (!this.selectedCustomer) return;

            this.loadHistory(this.selectedCustomer.id);
        },
        loadHistory(id) {
            this.historyLoading = id;
            axios.get('/api/reports/customers/' + id + '/activity').then(res => {
                this.activity = res.data.items || [];
                this.devices = res.data.devices || [];
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible cargar la actividad del cliente.'), {icon: 'mdi-alert-circle'});
            }).then(() => {
                this.historyLoading = null;
            });
        },
        statusColor(status) {
            if (Number(status) === 103) return 'success';
            if (Number(status) === 102) return 'info';
            if (Number(status) === 104) return 'error';

            return 'warning';
        },
        errorMessage(err, fallback) {
            if (err.response && err.response.data && err.response.data.status) {
                return err.response.data.status;
            }

            return fallback;
        }
    }
}
</script>

<style scoped>
.audit-fixed-dialog {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.audit-fixed-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.audit-fixed-footer {
    flex: 0 0 auto;
}
</style>
